import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Store, Image, Type, Palette, Link2,
  Navigation, LayoutGrid, FileText, Save, Upload,
  Instagram, Globe, Megaphone, Columns, Plus, Trash2,
  GripVertical, Eye, ChevronUp, ChevronDown, Bold, Italic, List, RefreshCw,
  Sparkles, Package, Layers, Wand2, Pencil, X, Monitor, Check, XCircle, ExternalLink,
  Mic
} from 'lucide-react';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { cn } from '@/lib/utils';
import { VoiceInput } from '@/components/ui/VoiceInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple Rich Text Editor Component
function SimpleRichTextEditor({ value, onChange, placeholder }) {
  const [showPreview, setShowPreview] = useState(false);
  
  const insertTag = (tag) => {
    const textarea = document.getElementById('rich-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    let newText;
    switch (tag) {
      case 'bold':
        newText = value.substring(0, start) + `<strong>${selectedText}</strong>` + value.substring(end);
        break;
      case 'italic':
        newText = value.substring(0, start) + `<em>${selectedText}</em>` + value.substring(end);
        break;
      case 'heading':
        newText = value.substring(0, start) + `<h2>${selectedText}</h2>` + value.substring(end);
        break;
      case 'list':
        newText = value.substring(0, start) + `<ul>\n<li>${selectedText}</li>\n</ul>` + value.substring(end);
        break;
      case 'link':
        newText = value.substring(0, start) + `<a href="#">${selectedText}</a>` + value.substring(end);
        break;
      default:
        newText = value;
    }
    onChange(newText);
  };
  
  return (
    <div className="border border-zinc-700 rounded-lg overflow-hidden">
      <div className="flex items-center gap-1 p-2 bg-zinc-800 border-b border-zinc-700">
        <Button variant="ghost" size="sm" onClick={() => insertTag('bold')} type="button">
          <Bold className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => insertTag('italic')} type="button">
          <Italic className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => insertTag('heading')} type="button">
          H2
        </Button>
        <Button variant="ghost" size="sm" onClick={() => insertTag('list')} type="button">
          <List className="w-4 h-4" />
        </Button>
        <div className="flex-1" />
        <Button 
          variant={showPreview ? "default" : "ghost"} 
          size="sm" 
          onClick={() => setShowPreview(!showPreview)}
          type="button"
        >
          <Eye className="w-4 h-4 mr-1" />
          Aperçu
        </Button>
      </div>
      {showPreview ? (
        <div 
          className="p-4 min-h-[200px] bg-white text-black prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: value || '<p class="text-gray-200">Aucun contenu</p>' }}
        />
      ) : (
        <Textarea
          id="rich-editor"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "Contenu HTML..."}
          className="min-h-[200px] bg-zinc-900 border-0 rounded-none resize-y font-mono text-sm"
        />
      )}
    </div>
  );
}

export function AdminBrandSettingsPage() {
  const navigate = useNavigate();
  const { isLight } = useAdminTheme();
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Lire le paramètre tab de l'URL pour sélectionner l'onglet
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'brand';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isSaving, setIsSaving] = useState(false);
  
  // States for each section
  const [brandData, setBrandData] = useState({
    store_name: { fr: '', he: '', en: '' },
    slogan: { fr: '', he: '', en: '' },
    logo_dark: null,
    logo_light: null,
    favicon: null
  });
  
  const [promoBanner, setPromoBanner] = useState({
    enabled: true,
    text: { fr: '', he: '', en: '' },
    background_color: '#C0000C',
    text_color: '#FFFFFF',
    link_url: '',
    link_target: '_self'
  });
  
  const [headerConfig, setHeaderConfig] = useState({
    nav_links: [],
    social_links: [
      { platform: 'instagram', url: '', enabled: false },
      { platform: 'tiktok', url: '', enabled: false },
      { platform: 'facebook', url: '', enabled: false }
    ],
    show_language_switch: true,
    show_currency_switch: true,
    background_color: '#0a0a0a',
    text_color: '#ffffff',
    nav_background_color: null,
    nav_text_color: null,
    // Ordre des sections: promo_banner, main_header, nav_bar
    sections_order: ['promo_banner', 'main_header', 'nav_bar']
  });
  
  const [sliderConfig, setSliderConfig] = useState({
    slides: [],
    autoplay: true,
    autoplay_speed: 5000,
    show_dots: true,
    show_arrows: true,
    // Options de typographie
    title_font: 'Oswald',
    title_font_size: '48',
    cta_font: 'Inter',
    cta_font_size: '16'
  });
  
  const [footerConfig, setFooterConfig] = useState({
    columns: [],
    copyright_text: { fr: '', he: '', en: '' },
    show_newsletter: true,
    newsletter_title: { fr: '', he: '', en: '' },
    newsletter_placeholder: { fr: '', he: '', en: '' }
  });
  
  const [staticPages, setStaticPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // États pour le générateur de slides IA
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [allAtmospheres, setAllAtmospheres] = useState([]);
  const [aiSelectedProducts, setAiSelectedProducts] = useState([]);
  const [aiSelectedAtmosphere, setAiSelectedAtmosphere] = useState('');
  const [aiStylePrompt, setAiStylePrompt] = useState('');
  const [aiVisualStyle, setAiVisualStyle] = useState('cinematic');
  const [aiGeneratedContent, setAiGeneratedContent] = useState(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  
  // État pour la modal d'édition de slide
  const [editingSlide, setEditingSlide] = useState(null);
  const [editingSlideIndex, setEditingSlideIndex] = useState(null);
  
  // État pour la prévisualisation plein écran
  const [previewSlide, setPreviewSlide] = useState(null);
  
  // Auth check
  useEffect(() => {
    const checkAuth = () => {
      const savedToken = localStorage.getItem('admin_token');
      if (savedToken) {
        setToken(savedToken);
        setIsAuthenticated(true);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);
  
  // Fetch all settings
  const fetchAllSettings = useCallback(async () => {
    if (!token) return;
    
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [brandRes, bannerRes, headerRes, sliderRes, footerRes, pagesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/settings/brand`, { headers }),
        fetch(`${API_URL}/api/admin/settings/promo-banner`, { headers }),
        fetch(`${API_URL}/api/admin/settings/header`, { headers }),
        fetch(`${API_URL}/api/admin/settings/slider`, { headers }),
        fetch(`${API_URL}/api/admin/settings/footer`, { headers }),
        fetch(`${API_URL}/api/admin/settings/pages`, { headers })
      ]);
      
      if (brandRes.ok) {
        const data = await brandRes.json();
        setBrandData(prev => ({ ...prev, ...data }));
      }
      if (bannerRes.ok) setPromoBanner(await bannerRes.json());
      if (headerRes.ok) setHeaderConfig(await headerRes.json());
      if (sliderRes.ok) setSliderConfig(await sliderRes.json());
      if (footerRes.ok) setFooterConfig(await footerRes.json());
      if (pagesRes.ok) {
        const data = await pagesRes.json();
        setStaticPages(data.pages || []);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, [token]);
  
  useEffect(() => {
    if (isAuthenticated) fetchAllSettings();
  }, [isAuthenticated, fetchAllSettings]);
  
  // Charger les polices Google Fonts dynamiquement
  useEffect(() => {
    const fonts = [
      'Oswald:wght@400;600;700',
      'Space+Grotesk:wght@400;500;600;700',
      'Playfair+Display:wght@400;600;700',
      'Bebas+Neue',
      'Inter:wght@400;500;600;700',
      'Poppins:wght@400;500;600;700',
      'Montserrat:wght@400;500;600;700',
      'Anton',
      'DM+Serif+Display',
      'Archivo+Black',
      'Syne:wght@400;500;600;700',
      'Outfit:wght@400;500;600;700',
      'Plus+Jakarta+Sans:wght@400;500;600;700',
      'Cormorant+Garamond:wght@400;600;700',
      'Work+Sans:wght@400;500;600;700',
      'Roboto:wght@400;500;700'
    ];
    
    // Vérifier si le lien existe déjà
    const existingLink = document.querySelector('link[data-fonts="admin-slider"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${fonts.join('&family=')}&display=swap`;
      link.setAttribute('data-fonts', 'admin-slider');
      document.head.appendChild(link);
    }
  }, []);
  
  // Charger les produits et atmosphères pour le générateur IA
  useEffect(() => {
    if (isAuthenticated && token) {
      const fetchProductsAndAtmospheres = async () => {
        try {
          const headers = { 'Authorization': `Bearer ${token}` };
          const [productsRes, atmosRes] = await Promise.all([
            fetch(`${API_URL}/api/products?limit=100`, { headers }),
            fetch(`${API_URL}/api/admin/atmospheres`, { headers })
          ]);
          
          if (productsRes.ok) {
            const data = await productsRes.json();
            setAllProducts(data.products || []);
          }
          if (atmosRes.ok) {
            const data = await atmosRes.json();
            setAllAtmospheres(data.atmospheres || []);
          }
        } catch (err) {
          console.error('Failed to fetch products/atmospheres:', err);
        }
      };
      fetchProductsAndAtmospheres();
    }
  }, [isAuthenticated, token]);
  
  // Fonction pour générer le slide avec IA
  const generateSlideWithAI = async () => {
    if (aiSelectedProducts.length === 0) {
      toast.error('Sélectionnez au moins un produit pour inspiration');
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ai/generate-slide`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_ids: aiSelectedProducts,
          atmosphere_id: aiSelectedAtmosphere || null,
          style_prompt: aiStylePrompt || null,
          visual_style: aiVisualStyle || 'cinematic'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.generated) {
          setAiGeneratedContent(data.generated);
          toast.success('Slide généré par l\'IA !');
        }
      } else {
        throw new Error('Generation failed');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      toast.error('Erreur lors de la génération IA');
    } finally {
      setAiLoading(false);
    }
  };
  
  // Fonction pour appliquer le contenu généré à un nouveau slide
  const applyAiGeneratedSlide = async () => {
    if (!aiGeneratedContent) return;
    
    const newSlide = {
      id: `slide-${Date.now()}`,
      enabled: true,
      title: aiGeneratedContent.title || { fr: '', he: '', en: '' },
      subtitle: aiGeneratedContent.subtitle || { fr: '', he: '', en: '' },
      cta_text: aiGeneratedContent.cta_text || { fr: 'Découvrir', he: 'גלה', en: 'Discover' },
      cta_url: aiGeneratedContent.cta_link || '/products',
      image_url: '',
      image_mobile_url: '',
      image_source: 'ai_generated',
      visual_style: aiGeneratedContent.visual_style || aiVisualStyle,
      duration_seconds: 5,
      order: sliderConfig.slides.length,
      image_position_y: 50,
      // Info du produit source pour affichage dans l'admin
      source_product_id: aiGeneratedContent.source_product_id || null,
      source_product_name: aiGeneratedContent.source_product_name || null,
      source_product_image: aiGeneratedContent.source_product_image || null,
      source_product_slug: aiGeneratedContent.source_product_slug || null
    };
    
    // Sauvegarder l'image DESKTOP si générée
    if (aiGeneratedContent.image_base64) {
      try {
        const saveRes = await fetch(`${API_URL}/api/admin/ai/save-generated-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            image_base64: aiGeneratedContent.image_base64,
            filename: `slide-${Date.now()}_desktop`
          })
        });
        
        if (saveRes.ok) {
          const saveData = await saveRes.json();
          newSlide.image_url = saveData.image_url;
        }
      } catch (err) {
        console.error('Failed to save desktop image:', err);
      }
    }
    
    // Sauvegarder l'image MOBILE si générée
    if (aiGeneratedContent.image_mobile_base64) {
      try {
        const saveMobileRes = await fetch(`${API_URL}/api/admin/ai/save-generated-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            image_base64: aiGeneratedContent.image_mobile_base64,
            filename: `slide-${Date.now()}_mobile`
          })
        });
        
        if (saveMobileRes.ok) {
          const saveMobileData = await saveMobileRes.json();
          newSlide.image_mobile_url = saveMobileData.image_url;
        }
      } catch (err) {
        console.error('Failed to save mobile image:', err);
      }
    }
    
    setSliderConfig(prev => ({
      ...prev,
      slides: [...prev.slides, newSlide]
    }));
    
    // Reset le dialogue
    setShowAiDialog(false);
    setAiGeneratedContent(null);
    setAiSelectedProducts([]);
    setAiSelectedAtmosphere('');
    setAiStylePrompt('');
    setAiVisualStyle('cinematic');
    
    toast.success('Slide ajouté avec images Desktop + Mobile !');
  };
  
  // Toggle sélection produit pour IA
  const toggleAiProductSelection = (productId) => {
    setAiSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };
  
  // Save handlers
  const saveBrand = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/brand`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(brandData)
      });
      
      if (response.ok) {
        toast.success('Identité visuelle sauvegardée');
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
  
  const savePromoBanner = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/promo-banner`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(promoBanner)
      });
      
      if (response.ok) {
        toast.success('Bannière promo sauvegardée');
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
  
  const saveHeader = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/header`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(headerConfig)
      });
      
      if (response.ok) {
        toast.success('Header sauvegardé');
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
  
  const saveSlider = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/slider`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sliderConfig)
      });
      
      if (response.ok) {
        toast.success('Slider sauvegardé');
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
  
  const saveFooter = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/footer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(footerConfig)
      });
      
      if (response.ok) {
        toast.success('Footer sauvegardé');
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
  
  const savePage = async (page) => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/pages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(page)
      });
      
      if (response.ok) {
        toast.success('Page sauvegardée');
        fetchAllSettings();
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
  
  // File upload handler - utilise l'API d'upload
  const handleFileUpload = async (field, e, folder = 'logos') => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Vérifier la taille (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }
    
    // Vérifier l'extension
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowedExts = ['png', 'jpg', 'jpeg', 'svg', 'ico', 'webp'];
    if (!allowedExts.includes(ext)) {
      toast.error(`Extension non autorisée. Utilisez: ${allowedExts.join(', ')}`);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/uploads/${folder}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const data = await response.json();
      
      // Mettre à jour le champ avec l'URL du fichier
      setBrandData(prev => ({ ...prev, [field]: data.url }));
      toast.success('Fichier uploadé');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(`Erreur d'upload: ${err.message}`);
    }
  };
  
  // Upload slider image
  const handleSliderImageUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/uploads/slider`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const data = await response.json();
      updateSlide(index, 'image_url', data.url);
      toast.success('Image uploadée');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(`Erreur d'upload: ${err.message}`);
    }
  };
  
  // Add new slide
  const addSlide = () => {
    const newSlide = {
      id: `slide-${Date.now()}`,
      image_url: '',
      image_base64: null,
      image_source: 'upload',
      title: { fr: 'Nouvelle slide', he: 'שקופית חדשה', en: 'New slide' },
      subtitle: { fr: '', he: '', en: '' },
      cta_text: { fr: 'Découvrir', he: 'גלה', en: 'Discover' },
      cta_url: '/',
      duration_seconds: 5,
      order: sliderConfig.slides.length,
      enabled: true
    };
    setSliderConfig(prev => ({
      ...prev,
      slides: [...prev.slides, newSlide]
    }));
  };
  
  // Update slide
  const updateSlide = (index, field, value, lang = null) => {
    setSliderConfig(prev => {
      const newSlides = [...prev.slides];
      if (lang) {
        newSlides[index] = {
          ...newSlides[index],
          [field]: { ...newSlides[index][field], [lang]: value }
        };
      } else {
        newSlides[index] = { ...newSlides[index], [field]: value };
      }
      return { ...prev, slides: newSlides };
    });
  };
  
  // Delete slide
  const deleteSlide = (index) => {
    setSliderConfig(prev => ({
      ...prev,
      slides: prev.slides.filter((_, i) => i !== index)
    }));
  };
  
  // Move slide
  const moveSlide = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sliderConfig.slides.length) return;
    
    setSliderConfig(prev => {
      const newSlides = [...prev.slides];
      [newSlides[index], newSlides[newIndex]] = [newSlides[newIndex], newSlides[index]];
      return { ...prev, slides: newSlides };
    });
  };
  
  // Ouvrir la modal d'édition de slide
  const openEditSlideModal = (index) => {
    setEditingSlide({ ...sliderConfig.slides[index] });
    setEditingSlideIndex(index);
  };
  
  // Fermer la modal d'édition
  const closeEditSlideModal = () => {
    setEditingSlide(null);
    setEditingSlideIndex(null);
  };
  
  // Sauvegarder les modifications du slide
  const saveEditedSlide = () => {
    if (editingSlideIndex !== null && editingSlide) {
      setSliderConfig(prev => {
        const newSlides = [...prev.slides];
        newSlides[editingSlideIndex] = editingSlide;
        return { ...prev, slides: newSlides };
      });
      toast.success('Slide modifié');
      closeEditSlideModal();
    }
  };
  
  // Mettre à jour un champ du slide en édition
  const updateEditingSlide = (field, value, lang = null) => {
    setEditingSlide(prev => {
      if (lang) {
        return {
          ...prev,
          [field]: { ...prev[field], [lang]: value }
        };
      }
      return { ...prev, [field]: value };
    });
  };
  
  // Add nav link
  const addNavLink = () => {
    const newLink = {
      id: `nav-${Date.now()}`,
      label: { fr: 'Nouveau lien', he: 'קישור חדש', en: 'New link' },
      url: '/',
      target: '_self',
      order: headerConfig.nav_links.length,
      enabled: true
    };
    setHeaderConfig(prev => ({
      ...prev,
      nav_links: [...prev.nav_links, newLink]
    }));
  };
  
  // Add footer column
  const addFooterColumn = () => {
    const newCol = {
      id: `col-${Date.now()}`,
      title: { fr: 'Nouvelle colonne', he: 'עמודה חדשה', en: 'New column' },
      links: [],
      order: footerConfig.columns.length
    };
    setFooterConfig(prev => ({
      ...prev,
      columns: [...prev.columns, newCol]
    }));
  };
  
  // Clear cache function
  const clearCache = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/cache`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to clear cache');
      
      // Clear localStorage cache on client side
      localStorage.removeItem('mutagim_store_settings');
      localStorage.removeItem('mutagim_store_settings_time');
      
      toast.success('Cache vidé — les changements sont visibles');
    } catch (err) {
      console.error('Error clearing cache:', err);
      toast.error('Erreur lors du vidage du cache');
    }
  };
  
  // Create new page
  const createNewPage = () => {
    const newPage = {
      slug: `page-${Date.now()}`,
      title: { fr: 'Nouvelle page', he: 'עמוד חדש', en: 'New page' },
      content: { fr: '', he: '', en: '' },
      meta_title: { fr: '', he: '', en: '' },
      meta_description: { fr: '', he: '', en: '' },
      enabled: true
    };
    setSelectedPage(newPage);
  };
  
  // Loading state
  if (isLoading) {
    return (
      <AdminPageWrapper title="Chargement...">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </AdminPageWrapper>
    );
  }
  
  // Not authenticated
  if (!isAuthenticated) {
    return (
      <AdminPageWrapper title="Accès Admin Requis">
        <div className="flex items-center justify-center">
          <Card className={cn("w-full max-w-md", isLight ? "bg-white border-gray-200" : "bg-zinc-900/80 border-zinc-800")}>
            <CardHeader className="text-center">
              <CardTitle className={cn("text-xl", isLight ? "text-gray-900" : "text-white")}>Accès Admin Requis</CardTitle>
              <CardDescription>Connectez-vous pour accéder aux paramètres</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/admin')} className="w-full">
                Aller au Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </AdminPageWrapper>
    );
  }
  
  return (
    <AdminPageWrapper title="Paramètres de la Marque" subtitle="Logo, couleurs, footer et pages statiques">
      <div className="max-w-7xl mx-auto">
        {/* Action buttons */}
        <div className="flex items-center justify-end gap-2 mb-6">
          <Button 
            onClick={clearCache} 
            variant="outline" 
            size="sm"
            className={isLight ? "text-amber-600 border-amber-400 hover:bg-amber-100" : "text-amber-400 border-amber-600 hover:bg-amber-600/20"}
            data-testid="clear-cache-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Rafraîchir le cache
          </Button>
        </div>
      
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={cn("p-1 grid grid-cols-6 w-full max-w-4xl", isLight ? "bg-gray-200" : "bg-zinc-800/50")}>
            <TabsTrigger value="brand" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Store className="w-4 h-4 mr-2" />
              Marque
            </TabsTrigger>
            <TabsTrigger value="banner" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Megaphone className="w-4 h-4 mr-2" />
              Bannière
            </TabsTrigger>
            <TabsTrigger value="header" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Navigation className="w-4 h-4 mr-2" />
              Header
            </TabsTrigger>
            <TabsTrigger value="slider" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <LayoutGrid className="w-4 h-4 mr-2" />
              Slider
            </TabsTrigger>
            <TabsTrigger value="footer" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Columns className="w-4 h-4 mr-2" />
              Footer
            </TabsTrigger>
            <TabsTrigger value="pages" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Pages
            </TabsTrigger>
          </TabsList>
          
          {/* BRAND TAB */}
          <TabsContent value="brand">
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Store className="w-5 h-5 text-primary" />
                  Identité Visuelle
                </CardTitle>
                <CardDescription>Logo, favicon, nom et slogan de votre boutique</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Store name */}
                <div>
                  <Label className="text-white mb-2 block">Nom de la boutique</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-gray-200">FR</Label>
                      <Input
                        value={brandData.store_name?.fr || ''}
                        onChange={(e) => setBrandData(prev => ({
                          ...prev,
                          store_name: { ...prev.store_name, fr: e.target.value }
                        }))}
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="brand-name-fr"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200">HE</Label>
                      <Input
                        dir="rtl"
                        value={brandData.store_name?.he || ''}
                        onChange={(e) => setBrandData(prev => ({
                          ...prev,
                          store_name: { ...prev.store_name, he: e.target.value }
                        }))}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200">EN</Label>
                      <Input
                        value={brandData.store_name?.en || ''}
                        onChange={(e) => setBrandData(prev => ({
                          ...prev,
                          store_name: { ...prev.store_name, en: e.target.value }
                        }))}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Slogan */}
                <div>
                  <Label className="text-white mb-2 block">Slogan</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      value={brandData.slogan?.fr || ''}
                      onChange={(e) => setBrandData(prev => ({
                        ...prev,
                        slogan: { ...prev.slogan, fr: e.target.value }
                      }))}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="FR"
                    />
                    <Input
                      dir="rtl"
                      value={brandData.slogan?.he || ''}
                      onChange={(e) => setBrandData(prev => ({
                        ...prev,
                        slogan: { ...prev.slogan, he: e.target.value }
                      }))}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="HE"
                    />
                    <Input
                      value={brandData.slogan?.en || ''}
                      onChange={(e) => setBrandData(prev => ({
                        ...prev,
                        slogan: { ...prev.slogan, en: e.target.value }
                      }))}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="EN"
                    />
                  </div>
                </div>
                
                {/* Logo uploads */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white mb-2 block">Logo (fond sombre)</Label>
                    <div className="border-2 border-dashed border-zinc-700 rounded-lg p-4 text-center">
                      {brandData.logo_dark ? (
                        <img 
                          src={brandData.logo_dark.startsWith('/api/') ? `${API_URL}${brandData.logo_dark}` : brandData.logo_dark} 
                          alt="Logo dark" 
                          className="h-16 mx-auto mb-2 object-contain" 
                        />
                      ) : (
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                      )}
                      <Input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={(e) => handleFileUpload('logo_dark', e, 'logos')}
                        className="text-xs"
                        data-testid="upload-logo-dark"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Logo (fond clair)</Label>
                    <div className="border-2 border-dashed border-zinc-700 rounded-lg p-4 text-center">
                      {brandData.logo_light ? (
                        <img 
                          src={brandData.logo_light.startsWith('/api/') ? `${API_URL}${brandData.logo_light}` : brandData.logo_light} 
                          alt="Logo light" 
                          className="h-16 mx-auto mb-2 object-contain bg-white rounded" 
                        />
                      ) : (
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                      )}
                      <Input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={(e) => handleFileUpload('logo_light', e, 'logos')}
                        className="text-xs"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Favicon</Label>
                    <div className="border-2 border-dashed border-zinc-700 rounded-lg p-4 text-center">
                      {brandData.favicon ? (
                        <img 
                          src={brandData.favicon.startsWith('/api/') ? `${API_URL}${brandData.favicon}` : brandData.favicon} 
                          alt="Favicon" 
                          className="w-8 h-8 mx-auto mb-2" 
                        />
                      ) : (
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                      )}
                      <Input
                        type="file"
                        accept=".ico,image/png,image/svg+xml"
                        onChange={(e) => handleFileUpload('favicon', e, 'favicons')}
                        className="text-xs"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Fixed colors */}
                <div className="p-4 bg-zinc-800/30 rounded-lg">
                  <Label className="text-white mb-2 block">Couleurs (fixées)</Label>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-[#C0000C]" />
                      <span className="text-sm text-white">Primary</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-[#1A1A1A]" />
                      <span className="text-sm text-white">Dark</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-white border border-zinc-600" />
                      <span className="text-sm text-white">White</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-200 mt-2">Les couleurs ne peuvent pas être modifiées</p>
                </div>
                
                <Button onClick={saveBrand} disabled={isSaving} data-testid="save-brand-button">
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* BANNER TAB */}
          <TabsContent value="banner">
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-primary" />
                  Bannière Promotionnelle
                </CardTitle>
                <CardDescription>Bannière affichée en haut du header</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                  <div>
                    <div className="text-white font-medium">Activer la bannière</div>
                    <div className="text-sm text-gray-200">Afficher ou masquer la bannière promo</div>
                  </div>
                  <Switch
                    checked={promoBanner.enabled}
                    onCheckedChange={(checked) => setPromoBanner(prev => ({ ...prev, enabled: checked }))}
                    data-testid="banner-enabled-switch"
                  />
                </div>
                
                <div>
                  <Label className="text-white mb-2 block">Texte de la bannière</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      value={promoBanner.text?.fr || ''}
                      onChange={(e) => setPromoBanner(prev => ({
                        ...prev,
                        text: { ...prev.text, fr: e.target.value }
                      }))}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="FR"
                      data-testid="banner-text-fr"
                    />
                    <Input
                      dir="rtl"
                      value={promoBanner.text?.he || ''}
                      onChange={(e) => setPromoBanner(prev => ({
                        ...prev,
                        text: { ...prev.text, he: e.target.value }
                      }))}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="HE"
                    />
                    <Input
                      value={promoBanner.text?.en || ''}
                      onChange={(e) => setPromoBanner(prev => ({
                        ...prev,
                        text: { ...prev.text, en: e.target.value }
                      }))}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="EN"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white mb-2 block">Couleur de fond</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={promoBanner.background_color}
                        onChange={(e) => setPromoBanner(prev => ({ ...prev, background_color: e.target.value }))}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={promoBanner.background_color}
                        onChange={(e) => setPromoBanner(prev => ({ ...prev, background_color: e.target.value }))}
                        className="bg-zinc-800 border-zinc-700 flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Couleur du texte</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={promoBanner.text_color}
                        onChange={(e) => setPromoBanner(prev => ({ ...prev, text_color: e.target.value }))}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={promoBanner.text_color}
                        onChange={(e) => setPromoBanner(prev => ({ ...prev, text_color: e.target.value }))}
                        className="bg-zinc-800 border-zinc-700 flex-1"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label className="text-white mb-2 block">Lien (optionnel)</Label>
                  <Input
                    value={promoBanner.link_url || ''}
                    onChange={(e) => setPromoBanner(prev => ({ ...prev, link_url: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="https://..."
                  />
                </div>
                
                {/* Preview */}
                <div>
                  <Label className="text-white mb-2 block">Aperçu</Label>
                  <div
                    className="py-2 px-4 text-center text-sm"
                    style={{
                      backgroundColor: promoBanner.background_color,
                      color: promoBanner.text_color
                    }}
                  >
                    {promoBanner.text?.fr || 'Texte de la bannière'}
                  </div>
                </div>
                
                <Button onClick={savePromoBanner} disabled={isSaving}>
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* HEADER TAB */}
          <TabsContent value="header">
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-primary" />
                  Configuration du Header
                </CardTitle>
                <CardDescription>Navigation, réseaux sociaux et options d'affichage</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Switches */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                    <span className="text-white">Switch de langue</span>
                    <Switch
                      checked={headerConfig.show_language_switch}
                      onCheckedChange={(checked) => setHeaderConfig(prev => ({ ...prev, show_language_switch: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                    <span className="text-white">Switch de devise</span>
                    <Switch
                      checked={headerConfig.show_currency_switch}
                      onCheckedChange={(checked) => setHeaderConfig(prev => ({ ...prev, show_currency_switch: checked }))}
                    />
                  </div>
                </div>
                
                {/* Ordre des sections du header - Drag & Drop */}
                <div className="p-4 bg-zinc-800/30 rounded-lg space-y-4">
                  <Label className="text-white flex items-center gap-2">
                    <Layers className="w-4 h-4 text-primary" />
                    Ordre des sections (glisser-déposer)
                  </Label>
                  <p className="text-xs text-gray-400">Réorganisez l'ordre d'affichage des 3 sections du header</p>
                  
                  <div className="space-y-2">
                    {(headerConfig.sections_order || ['promo_banner', 'main_header', 'nav_bar']).map((sectionId, index) => {
                      const sectionLabels = {
                        promo_banner: { label: 'Bannière Promotionnelle', icon: '🏷️', desc: 'Ex: "SALE -30% sitewide"' },
                        main_header: { label: 'Header Principal', icon: '🏠', desc: 'Logo, recherche, panier' },
                        nav_bar: { label: 'Barre de Navigation', icon: '📋', desc: 'Liens: Home, Products, Categories' }
                      };
                      const section = sectionLabels[sectionId];
                      
                      return (
                        <div
                          key={sectionId}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', index.toString());
                          }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                            const toIndex = index;
                            if (fromIndex !== toIndex) {
                              const newOrder = [...(headerConfig.sections_order || ['promo_banner', 'main_header', 'nav_bar'])];
                              const [moved] = newOrder.splice(fromIndex, 1);
                              newOrder.splice(toIndex, 0, moved);
                              setHeaderConfig(prev => ({ ...prev, sections_order: newOrder }));
                            }
                          }}
                          className="flex items-center gap-3 p-3 bg-zinc-700/50 rounded-lg border border-zinc-600 cursor-move hover:bg-zinc-700 transition-colors group"
                        >
                          <div className="flex items-center justify-center w-8 h-8 rounded bg-zinc-600 text-white font-bold text-sm">
                            {index + 1}
                          </div>
                          <span className="text-xl">{section?.icon}</span>
                          <div className="flex-1">
                            <p className="text-white font-medium text-sm">{section?.label}</p>
                            <p className="text-gray-400 text-xs">{section?.desc}</p>
                          </div>
                          <div className="text-gray-500 group-hover:text-gray-300">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                            </svg>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Aperçu de l'ordre */}
                  <div className="mt-3 p-3 bg-zinc-900/50 rounded border border-zinc-700">
                    <p className="text-xs text-gray-400 mb-2">Aperçu de l'ordre :</p>
                    <div className="flex flex-col gap-1">
                      {(headerConfig.sections_order || ['promo_banner', 'main_header', 'nav_bar']).map((sectionId, i) => {
                        const colors = {
                          promo_banner: 'bg-red-600',
                          main_header: 'bg-zinc-800',
                          nav_bar: 'bg-zinc-700'
                        };
                        const names = {
                          promo_banner: 'Promo',
                          main_header: 'Header',
                          nav_bar: 'Nav'
                        };
                        return (
                          <div 
                            key={sectionId} 
                            className={`${colors[sectionId]} text-white text-xs py-1 px-2 rounded text-center`}
                          >
                            {names[sectionId]}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                
                {/* Couleurs du Header - 2 zones séparées */}
                <div className="p-4 bg-zinc-800/30 rounded-lg space-y-6">
                  <Label className="text-white flex items-center gap-2">
                    <Palette className="w-4 h-4 text-primary" />
                    Couleurs du Header (2 zones)
                  </Label>
                  
                  {/* Zone 1: Header Principal */}
                  <div className="space-y-3">
                    <Label className="text-gray-200 text-sm font-medium">Zone 1 : Header Principal (Logo, Recherche, Actions)</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-300 text-xs">Couleur de fond</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={headerConfig.background_color || '#0a0a0a'}
                            onChange={(e) => setHeaderConfig(prev => ({ ...prev, background_color: e.target.value }))}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={headerConfig.background_color || '#0a0a0a'}
                            onChange={(e) => setHeaderConfig(prev => ({ ...prev, background_color: e.target.value }))}
                            className="bg-zinc-700 border-zinc-600 flex-1 font-mono"
                            placeholder="#0a0a0a"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300 text-xs">Couleur du texte</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={headerConfig.text_color || '#ffffff'}
                            onChange={(e) => setHeaderConfig(prev => ({ ...prev, text_color: e.target.value }))}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={headerConfig.text_color || '#ffffff'}
                            onChange={(e) => setHeaderConfig(prev => ({ ...prev, text_color: e.target.value }))}
                            className="bg-zinc-700 border-zinc-600 flex-1 font-mono"
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Zone 2: Barre de Navigation */}
                  <div className="space-y-3 pt-4 border-t border-zinc-700">
                    <Label className="text-gray-200 text-sm font-medium">Zone 2 : Barre de Navigation (Liens du menu)</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-300 text-xs">Couleur de fond</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={headerConfig.nav_background_color || headerConfig.background_color || '#0a0a0a'}
                            onChange={(e) => setHeaderConfig(prev => ({ ...prev, nav_background_color: e.target.value }))}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={headerConfig.nav_background_color || headerConfig.background_color || '#0a0a0a'}
                            onChange={(e) => setHeaderConfig(prev => ({ ...prev, nav_background_color: e.target.value }))}
                            className="bg-zinc-700 border-zinc-600 flex-1 font-mono"
                            placeholder="#0a0a0a"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300 text-xs">Couleur du texte</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={headerConfig.nav_text_color || headerConfig.text_color || '#ffffff'}
                            onChange={(e) => setHeaderConfig(prev => ({ ...prev, nav_text_color: e.target.value }))}
                            className="w-12 h-10 rounded cursor-pointer border-0"
                          />
                          <Input
                            value={headerConfig.nav_text_color || headerConfig.text_color || '#ffffff'}
                            onChange={(e) => setHeaderConfig(prev => ({ ...prev, nav_text_color: e.target.value }))}
                            className="bg-zinc-700 border-zinc-600 flex-1 font-mono"
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Aperçu combiné */}
                  <div className="space-y-1 mt-4">
                    <Label className="text-gray-300 text-xs">Aperçu</Label>
                    <div className="rounded-lg overflow-hidden border border-zinc-600">
                      <div 
                        className="h-10 flex items-center justify-center text-xs font-medium"
                        style={{ 
                          backgroundColor: headerConfig.background_color || '#0a0a0a',
                          color: headerConfig.text_color || '#ffffff'
                        }}
                      >
                        MUTAGIM · Recherche · Langue · Panier
                      </div>
                      <div 
                        className="h-8 flex items-center justify-center text-xs font-medium border-t border-zinc-600"
                        style={{ 
                          backgroundColor: headerConfig.nav_background_color || headerConfig.background_color || '#0a0a0a',
                          color: headerConfig.nav_text_color || headerConfig.text_color || '#ffffff'
                        }}
                      >
                        Home · Products · Categories
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Nav links */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-white">Liens de navigation</Label>
                    <Button variant="outline" size="sm" onClick={addNavLink}>
                      <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {headerConfig.nav_links?.map((link, index) => (
                      <div key={link.id} className="flex items-center gap-2 p-3 bg-zinc-800/30 rounded-lg">
                        <GripVertical className="w-4 h-4 text-gray-200 cursor-move" />
                        <Input
                          value={link.label?.fr || ''}
                          onChange={(e) => {
                            const newLinks = [...headerConfig.nav_links];
                            newLinks[index] = { ...link, label: { ...link.label, fr: e.target.value } };
                            setHeaderConfig(prev => ({ ...prev, nav_links: newLinks }));
                          }}
                          className="bg-zinc-700 border-zinc-600 flex-1"
                          placeholder="Label FR"
                        />
                        <Input
                          value={link.url || ''}
                          onChange={(e) => {
                            const newLinks = [...headerConfig.nav_links];
                            newLinks[index] = { ...link, url: e.target.value };
                            setHeaderConfig(prev => ({ ...prev, nav_links: newLinks }));
                          }}
                          className="bg-zinc-700 border-zinc-600 w-32"
                          placeholder="URL"
                        />
                        <Switch
                          checked={link.enabled}
                          onCheckedChange={(checked) => {
                            const newLinks = [...headerConfig.nav_links];
                            newLinks[index] = { ...link, enabled: checked };
                            setHeaderConfig(prev => ({ ...prev, nav_links: newLinks }));
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newLinks = headerConfig.nav_links.filter((_, i) => i !== index);
                            setHeaderConfig(prev => ({ ...prev, nav_links: newLinks }));
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Social links */}
                <div>
                  <Label className="text-white mb-3 block">Réseaux sociaux</Label>
                  <div className="space-y-2">
                    {headerConfig.social_links?.map((social, index) => (
                      <div key={social.platform} className="flex items-center gap-2 p-3 bg-zinc-800/30 rounded-lg">
                        <span className="w-24 text-white capitalize">{social.platform}</span>
                        <Input
                          value={social.url || ''}
                          onChange={(e) => {
                            const newSocials = [...headerConfig.social_links];
                            newSocials[index] = { ...social, url: e.target.value };
                            setHeaderConfig(prev => ({ ...prev, social_links: newSocials }));
                          }}
                          className="bg-zinc-700 border-zinc-600 flex-1"
                          placeholder={`URL ${social.platform}`}
                        />
                        <Switch
                          checked={social.enabled}
                          onCheckedChange={(checked) => {
                            const newSocials = [...headerConfig.social_links];
                            newSocials[index] = { ...social, enabled: checked };
                            setHeaderConfig(prev => ({ ...prev, social_links: newSocials }));
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                
                <Button onClick={saveHeader} disabled={isSaving}>
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* SLIDER TAB */}
          <TabsContent value="slider">
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <LayoutGrid className="w-5 h-5 text-primary" />
                  Hero Slider
                </CardTitle>
                <CardDescription>Configurez les slides du carrousel principal</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Options */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                    <span className="text-white text-sm">Autoplay</span>
                    <Switch
                      checked={sliderConfig.autoplay}
                      onCheckedChange={(checked) => setSliderConfig(prev => ({ ...prev, autoplay: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                    <span className="text-white text-sm">Points</span>
                    <Switch
                      checked={sliderConfig.show_dots}
                      onCheckedChange={(checked) => setSliderConfig(prev => ({ ...prev, show_dots: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                    <span className="text-white text-sm">Flèches</span>
                    <Switch
                      checked={sliderConfig.show_arrows}
                      onCheckedChange={(checked) => setSliderConfig(prev => ({ ...prev, show_arrows: checked }))}
                    />
                  </div>
                  <div className="p-3 bg-zinc-800/30 rounded-lg">
                    <Label className="text-xs text-gray-200 mb-1 block">Vitesse (ms)</Label>
                    <Input
                      type="number"
                      value={sliderConfig.autoplay_speed}
                      onChange={(e) => setSliderConfig(prev => ({ ...prev, autoplay_speed: parseInt(e.target.value) }))}
                      className="bg-zinc-700 border-zinc-600 h-8"
                    />
                  </div>
                </div>
                
                {/* Typographie */}
                <div className="p-4 bg-zinc-800/30 rounded-lg space-y-4">
                  <Label className="text-white flex items-center gap-2">
                    <Type className="w-4 h-4 text-primary" />
                    Typographie des slides
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Police titre</Label>
                      <select
                        value={sliderConfig.title_font || 'Clash Display'}
                        onChange={(e) => setSliderConfig(prev => ({ ...prev, title_font: e.target.value }))}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white text-sm"
                      >
                        <optgroup label="Modernes & Bold">
                          <option value="Space Grotesk">Space Grotesk</option>
                          <option value="Syne">Syne</option>
                          <option value="Outfit">Outfit</option>
                          <option value="Plus Jakarta Sans">Plus Jakarta Sans</option>
                          <option value="Work Sans">Work Sans</option>
                        </optgroup>
                        <optgroup label="Élégantes">
                          <option value="Playfair Display">Playfair Display</option>
                          <option value="Cormorant Garamond">Cormorant Garamond</option>
                          <option value="DM Serif Display">DM Serif Display</option>
                        </optgroup>
                        <optgroup label="Impact">
                          <option value="Anton">Anton</option>
                          <option value="Bebas Neue">Bebas Neue</option>
                          <option value="Oswald">Oswald</option>
                          <option value="Archivo Black">Archivo Black</option>
                        </optgroup>
                        <optgroup label="Classiques">
                          <option value="Montserrat">Montserrat</option>
                          <option value="Poppins">Poppins</option>
                          <option value="Inter">Inter</option>
                          <option value="Roboto">Roboto</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Taille titre (px)</Label>
                      <Input
                        type="number"
                        value={sliderConfig.title_font_size || 48}
                        onChange={(e) => setSliderConfig(prev => ({ ...prev, title_font_size: e.target.value }))}
                        className="bg-zinc-700 border-zinc-600 h-8"
                        min={24}
                        max={120}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Police CTA</Label>
                      <select
                        value={sliderConfig.cta_font || 'Space Grotesk'}
                        onChange={(e) => setSliderConfig(prev => ({ ...prev, cta_font: e.target.value }))}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white text-sm"
                      >
                        <option value="Space Grotesk">Space Grotesk</option>
                        <option value="Inter">Inter</option>
                        <option value="Plus Jakarta Sans">Plus Jakarta Sans</option>
                        <option value="Outfit">Outfit</option>
                        <option value="Montserrat">Montserrat</option>
                        <option value="Poppins">Poppins</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Taille CTA (px)</Label>
                      <Input
                        type="number"
                        value={sliderConfig.cta_font_size || 16}
                        onChange={(e) => setSliderConfig(prev => ({ ...prev, cta_font_size: e.target.value }))}
                        className="bg-zinc-700 border-zinc-600 h-8"
                        min={12}
                        max={32}
                      />
                    </div>
                  </div>
                  
                  {/* Couleurs des textes */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-zinc-700">
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Couleur titre</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={sliderConfig.title_color || '#FFFFFF'}
                          onChange={(e) => setSliderConfig(prev => ({ ...prev, title_color: e.target.value }))}
                          className="w-10 h-8 rounded border border-zinc-600 cursor-pointer bg-transparent"
                        />
                        <Input
                          type="text"
                          value={sliderConfig.title_color || '#FFFFFF'}
                          onChange={(e) => setSliderConfig(prev => ({ ...prev, title_color: e.target.value }))}
                          className="bg-zinc-700 border-zinc-600 h-8 flex-1 text-xs"
                          placeholder="#FFFFFF"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Couleur sous-titre</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={sliderConfig.subtitle_color || '#D1D5DB'}
                          onChange={(e) => setSliderConfig(prev => ({ ...prev, subtitle_color: e.target.value }))}
                          className="w-10 h-8 rounded border border-zinc-600 cursor-pointer bg-transparent"
                        />
                        <Input
                          type="text"
                          value={sliderConfig.subtitle_color || '#D1D5DB'}
                          onChange={(e) => setSliderConfig(prev => ({ ...prev, subtitle_color: e.target.value }))}
                          className="bg-zinc-700 border-zinc-600 h-8 flex-1 text-xs"
                          placeholder="#D1D5DB"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Couleur CTA (texte)</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={sliderConfig.cta_text_color || '#000000'}
                          onChange={(e) => setSliderConfig(prev => ({ ...prev, cta_text_color: e.target.value }))}
                          className="w-10 h-8 rounded border border-zinc-600 cursor-pointer bg-transparent"
                        />
                        <Input
                          type="text"
                          value={sliderConfig.cta_text_color || '#000000'}
                          onChange={(e) => setSliderConfig(prev => ({ ...prev, cta_text_color: e.target.value }))}
                          className="bg-zinc-700 border-zinc-600 h-8 flex-1 text-xs"
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Couleur CTA (fond)</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={sliderConfig.cta_bg_color || '#FFFFFF'}
                          onChange={(e) => setSliderConfig(prev => ({ ...prev, cta_bg_color: e.target.value }))}
                          className="w-10 h-8 rounded border border-zinc-600 cursor-pointer bg-transparent"
                        />
                        <Input
                          type="text"
                          value={sliderConfig.cta_bg_color || '#FFFFFF'}
                          onChange={(e) => setSliderConfig(prev => ({ ...prev, cta_bg_color: e.target.value }))}
                          className="bg-zinc-700 border-zinc-600 h-8 flex-1 text-xs"
                          placeholder="#FFFFFF"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Effets de style */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-zinc-700">
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Effet titre</Label>
                      <select
                        value={sliderConfig.title_effect || 'none'}
                        onChange={(e) => setSliderConfig(prev => ({ ...prev, title_effect: e.target.value }))}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white text-sm"
                      >
                        <option value="none">Aucun</option>
                        <option value="shadow">Ombre portée</option>
                        <option value="glow">Lueur (Glow)</option>
                        <option value="outline">Contour</option>
                        <option value="gradient">Dégradé</option>
                        <option value="neon">Néon</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Effet sous-titre</Label>
                      <select
                        value={sliderConfig.subtitle_effect || 'none'}
                        onChange={(e) => setSliderConfig(prev => ({ ...prev, subtitle_effect: e.target.value }))}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white text-sm"
                      >
                        <option value="none">Aucun</option>
                        <option value="shadow">Ombre portée</option>
                        <option value="glow">Lueur (Glow)</option>
                        <option value="outline">Contour</option>
                        <option value="neon">Néon</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Style CTA</Label>
                      <select
                        value={sliderConfig.cta_style || 'solid'}
                        onChange={(e) => setSliderConfig(prev => ({ ...prev, cta_style: e.target.value }))}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white text-sm"
                      >
                        <option value="solid">Plein</option>
                        <option value="outline">Contour</option>
                        <option value="gradient">Dégradé</option>
                        <option value="glass">Verre (Glass)</option>
                        <option value="neon">Néon</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Animation titre</Label>
                      <select
                        value={sliderConfig.title_animation || 'fade'}
                        onChange={(e) => setSliderConfig(prev => ({ ...prev, title_animation: e.target.value }))}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white text-sm"
                      >
                        <option value="fade">Fondu</option>
                        <option value="slide-up">Glisser vers le haut</option>
                        <option value="slide-left">Glisser de gauche</option>
                        <option value="zoom">Zoom</option>
                        <option value="typewriter">Machine à écrire</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-200 mb-1 block">Emoji dans titre</Label>
                      <div className="flex gap-1 flex-wrap">
                        {['🔥', '✨', '💎', '🚀', '💯', '⚡', '🌟', '💫'].map(emoji => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => {
                              // Copier l'emoji dans le presse-papier
                              navigator.clipboard.writeText(emoji);
                              toast.success(`${emoji} copié ! Collez-le dans votre titre`);
                            }}
                            className="w-7 h-7 bg-zinc-600 hover:bg-zinc-500 rounded text-lg flex items-center justify-center"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Zoom des images */}
                  <div className="pt-3 border-t border-zinc-700">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs text-gray-200">Zoom des images</Label>
                      <span className="text-xs text-gray-400">
                        {sliderConfig.image_zoom <= 50 ? 'Image entière' : 
                         sliderConfig.image_zoom <= 100 ? 'Couvre l\'écran' : 
                         `Zoomé +${sliderConfig.image_zoom - 100}%`}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="50"
                      max="150"
                      step="10"
                      value={sliderConfig.image_zoom || 100}
                      onChange={(e) => setSliderConfig(prev => ({ ...prev, image_zoom: parseInt(e.target.value) }))}
                      className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                      <span>50% (image entière)</span>
                      <span>100% (couvre)</span>
                      <span>150% (zoomé)</span>
                    </div>
                  </div>
                  
                  {/* Aperçu de la typographie - AMÉLIORÉ */}
                  <div className="p-4 bg-zinc-900/50 rounded border border-zinc-700 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-gray-400 text-xs">Aperçu en temps réel:</p>
                      <p className="text-[10px] text-gray-500">Police: {sliderConfig.title_font || 'Oswald'}</p>
                    </div>
                    
                    {/* Aperçu grand format simulant le slider */}
                    <div className="relative w-full aspect-[16/6] bg-gradient-to-br from-zinc-800 via-zinc-900 to-black rounded-lg overflow-hidden border border-zinc-600">
                      {/* Image de fond si un slide existe */}
                      {sliderConfig.slides?.[0]?.image_url && (
                        <div 
                          className="absolute inset-0 opacity-40"
                          style={{
                            backgroundImage: `url(${sliderConfig.slides[0].image_url.startsWith('/api/') ? `${API_URL}${sliderConfig.slides[0].image_url}` : sliderConfig.slides[0].image_url})`,
                            backgroundSize: 'cover',
                            backgroundPosition: `center ${sliderConfig.slides[0].image_position_y || 50}%`
                          }}
                        />
                      )}
                      
                      {/* Contenu texte */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
                        <p 
                          className="mb-3 leading-tight"
                          style={{ 
                            fontFamily: sliderConfig.title_font || 'Oswald', 
                            fontSize: `${Math.min(sliderConfig.title_font_size || 48, 42)}px`,
                            fontWeight: 600,
                            color: sliderConfig.title_color || '#FFFFFF',
                            textShadow: sliderConfig.title_effect === 'shadow' ? '2px 2px 4px rgba(0,0,0,0.5)' :
                                        sliderConfig.title_effect === 'glow' ? `0 0 10px ${sliderConfig.title_color || '#FFFFFF'}80` :
                                        sliderConfig.title_effect === 'neon' ? '0 0 10px #ff0066, 0 0 20px #ff0066' : '2px 2px 8px rgba(0,0,0,0.8)',
                            WebkitTextStroke: sliderConfig.title_effect === 'outline' ? `1px ${sliderConfig.title_color || '#FFFFFF'}` : 'none',
                            background: sliderConfig.title_effect === 'gradient' ? 'linear-gradient(90deg, #ff0066, #00ccff)' : 'none',
                            WebkitBackgroundClip: sliderConfig.title_effect === 'gradient' ? 'text' : 'unset',
                            WebkitTextFillColor: sliderConfig.title_effect === 'gradient' ? 'transparent' : 'unset'
                          }}
                        >
                          Collection Exclusive 🔥
                        </p>
                        <p 
                          className="text-sm mb-4 max-w-md"
                          style={{ color: sliderConfig.subtitle_color || '#D1D5DB' }}
                        >
                          Découvrez notre nouvelle collection streetwear premium
                        </p>
                        <button 
                          className={cn(
                            "px-6 py-2 rounded transition-all",
                            sliderConfig.cta_style === 'outline' && "border-2 bg-transparent",
                            sliderConfig.cta_style === 'gradient' && "bg-gradient-to-r from-pink-500 to-purple-500",
                            sliderConfig.cta_style === 'glass' && "bg-white/10 backdrop-blur border border-white/20",
                            sliderConfig.cta_style === 'neon' && "bg-transparent border-2 border-pink-500 shadow-[0_0_10px_#ff0066]"
                          )}
                          style={{ 
                            fontFamily: sliderConfig.cta_font || 'Inter', 
                            fontSize: `${sliderConfig.cta_font_size || 16}px`,
                            fontWeight: 500,
                            color: sliderConfig.cta_text_color || '#000000',
                            backgroundColor: sliderConfig.cta_style === 'solid' || !sliderConfig.cta_style ? (sliderConfig.cta_bg_color || '#FFFFFF') : undefined,
                            borderColor: sliderConfig.cta_style === 'outline' ? (sliderConfig.cta_bg_color || '#FFFFFF') : undefined
                          }}
                        >
                          Découvrir ✨
                        </button>
                      </div>
                    </div>
                    
                    {/* Info polices */}
                    <div className="flex justify-between text-[10px] text-gray-500">
                      <span>Titre: {sliderConfig.title_font} ({sliderConfig.title_font_size}px)</span>
                      <span>CTA: {sliderConfig.cta_font} ({sliderConfig.cta_font_size}px)</span>
                    </div>
                  </div>
                </div>
                
                {/* Aperçu des slides en grille */}
                {sliderConfig.slides?.length > 0 && (
                  <div className="p-4 bg-zinc-800/30 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-white flex items-center gap-2">
                        <Eye className="w-4 h-4 text-primary" />
                        Aperçu des slides ({sliderConfig.slides.filter(s => s.enabled !== false).length} en ligne / {sliderConfig.slides.length} total)
                      </Label>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                      {sliderConfig.slides.map((slide, index) => {
                        const imgUrl = slide.image_url?.startsWith('/api/') 
                          ? `${API_URL}${slide.image_url}` 
                          : slide.image_url;
                        return (
                          <div 
                            key={slide.id || index}
                            className={cn(
                              "relative aspect-video rounded-lg overflow-hidden border-2 cursor-pointer transition-all",
                              slide.enabled 
                                ? "border-green-500/50 hover:border-green-500" 
                                : "border-zinc-700 opacity-50"
                            )}
                            onClick={() => {
                              // Scroll vers le slide correspondant
                              const slideEl = document.getElementById(`slide-editor-${index}`);
                              if (slideEl) slideEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }}
                          >
                            {imgUrl ? (
                              <div 
                                className="w-full h-full"
                                style={{
                                  backgroundImage: `url(${imgUrl})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: `center ${slide.image_position_y || 50}%`
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                                <Image className="w-8 h-8 text-zinc-600" />
                              </div>
                            )}
                            {/* Overlay avec titre */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-2">
                              <p className="text-white text-xs font-medium truncate">
                                {slide.title?.fr || `Slide ${index + 1}`}
                              </p>
                              <p className="text-gray-300 text-[10px] truncate">
                                {slide.cta_text?.fr || 'Pas de CTA'}
                              </p>
                            </div>
                            {/* Badge position */}
                            <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                              #{index + 1}
                            </div>
                            {/* Badge statut EN LIGNE / HORS LIGNE */}
                            <div className={cn(
                              "absolute top-1 right-1 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1",
                              slide.enabled ? "bg-green-500/80" : "bg-red-500/80"
                            )}>
                              {slide.enabled ? (
                                <><Check className="w-3 h-3" /> ON</>
                              ) : (
                                <><XCircle className="w-3 h-3" /> OFF</>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Slides */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-white">Slides ({sliderConfig.slides?.length || 0})</Label>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={addSlide} data-testid="add-slide-button">
                        <Plus className="w-4 h-4 mr-1" /> Ajouter manuellement
                      </Button>
                      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
                        <DialogTrigger asChild>
                          <Button size="sm" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                            <Wand2 className="w-4 h-4 mr-1" /> Générer avec IA
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="text-white flex items-center gap-2">
                              <Sparkles className="w-5 h-5 text-purple-400" />
                              Assistant IA - Génération de Slide
                            </DialogTitle>
                            <DialogDescription>
                              Sélectionnez des produits et une atmosphère pour générer automatiquement un slide
                            </DialogDescription>
                          </DialogHeader>
                          
                          <div className="space-y-6 py-4">
                            {/* Étape 1: Sélection des produits */}
                            <div className="space-y-3">
                              <Label className="text-white flex items-center gap-2">
                                <Package className="w-4 h-4 text-blue-400" />
                                1. Sélectionnez des produits pour inspiration ({aiSelectedProducts.length} sélectionné(s))
                              </Label>
                              
                              {/* Barre de recherche */}
                              <Input
                                value={productSearchQuery}
                                onChange={(e) => setProductSearchQuery(e.target.value)}
                                placeholder="Rechercher un produit..."
                                className="bg-zinc-800 border-zinc-700"
                              />
                              
                              {/* Grille de produits avec vignettes */}
                              <div className="grid grid-cols-4 md:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-2 bg-zinc-800/30 rounded-lg border border-zinc-700">
                                {allProducts
                                  .filter(p => {
                                    const productName = p.name?.fr || p.title || '';
                                    return !productSearchQuery || 
                                      productName.toLowerCase().includes(productSearchQuery.toLowerCase());
                                  })
                                  .slice(0, 24)
                                  .map(product => {
                                    const isSelected = aiSelectedProducts.includes(product.id);
                                    // Gérer la structure images: {main: [...], qc: [...], variants: [...]}
                                    let imgUrl = null;
                                    if (product.images) {
                                      if (Array.isArray(product.images)) {
                                        imgUrl = product.images[0];
                                      } else if (product.images.main && product.images.main.length > 0) {
                                        imgUrl = product.images.main[0];
                                      }
                                    }
                                    const fullImgUrl = imgUrl?.startsWith('/api/') ? `${API_URL}${imgUrl}` : imgUrl;
                                    const productName = product.name?.fr || product.title || 'Sans nom';
                                    
                                    return (
                                      <div
                                        key={product.id}
                                        onClick={() => toggleAiProductSelection(product.id)}
                                        className={cn(
                                          "cursor-pointer rounded-lg border-2 transition-all overflow-hidden",
                                          isSelected 
                                            ? "border-purple-500 ring-2 ring-purple-500/30 bg-purple-500/10" 
                                            : "border-zinc-700 hover:border-zinc-500"
                                        )}
                                      >
                                        <div className="aspect-square bg-zinc-800 overflow-hidden">
                                          {fullImgUrl ? (
                                            <img 
                                              src={fullImgUrl} 
                                              alt={productName} 
                                              className="w-full h-full object-cover"
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling?.style && (e.target.nextSibling.style.display = 'flex');
                                              }}
                                            />
                                          ) : null}
                                          <div className={`w-full h-full items-center justify-center ${fullImgUrl ? 'hidden' : 'flex'}`}>
                                            <Package className="w-6 h-6 text-zinc-600" />
                                          </div>
                                        </div>
                                        <p className="text-[10px] text-white truncate p-1 text-center">
                                          {productName.substring(0, 20)}
                                        </p>
                                        {isSelected && (
                                          <div className="bg-purple-500 text-white text-[10px] py-0.5 text-center">
                                            ✓
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                {allProducts.length === 0 && (
                                  <div className="col-span-full text-center py-8 text-gray-400">
                                    <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p>Chargement des produits...</p>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Étape 2: Sélection de l'atmosphère */}
                            <div className="space-y-3">
                              <Label className="text-white flex items-center gap-2">
                                <Layers className="w-4 h-4 text-green-400" />
                                2. Choisissez une atmosphère (optionnel)
                              </Label>
                              <select
                                value={aiSelectedAtmosphere}
                                onChange={(e) => setAiSelectedAtmosphere(e.target.value)}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                              >
                                <option value="">Aucune atmosphère</option>
                                {allAtmospheres.map(atmo => (
                                  <option key={atmo.id || atmo._id} value={atmo.id || atmo._id}>
                                    {atmo.name?.fr || atmo.name || 'Sans nom'}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            {/* Étape 3: Style visuel */}
                            <div className="space-y-3">
                              <Label className="text-white flex items-center gap-2">
                                <Palette className="w-4 h-4 text-pink-400" />
                                3. Style visuel
                              </Label>
                              <div className="grid grid-cols-3 gap-2">
                                {[
                                  { id: 'cinematic', name: 'Cinématique', desc: 'Style film, éclairage dramatique' },
                                  { id: 'editorial', name: 'Editorial', desc: 'Haute couture, studio pro' },
                                  { id: 'minimalist', name: 'Minimaliste', desc: 'Fond simple, épuré' },
                                  { id: 'dramatic', name: 'Dramatique', desc: 'Contrastes forts, intense' },
                                  { id: 'urban', name: 'Urbain', desc: 'Rue, streetwear' },
                                  { id: 'luxury', name: 'Luxe', desc: 'Élégant, premium' }
                                ].map(style => (
                                  <button
                                    key={style.id}
                                    onClick={() => setAiVisualStyle(style.id)}
                                    className={cn(
                                      "p-2 rounded-lg border text-left transition-all",
                                      aiVisualStyle === style.id
                                        ? "border-purple-500 bg-purple-500/20"
                                        : "border-zinc-700 bg-zinc-800 hover:border-zinc-600"
                                    )}
                                  >
                                    <p className="text-white text-sm font-medium">{style.name}</p>
                                    <p className="text-[10px] text-gray-400">{style.desc}</p>
                                  </button>
                                ))}
                              </div>
                            </div>
                            
                            {/* Étape 4: Instructions personnalisées */}
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <Label className="text-white flex items-center gap-2">
                                  <Type className="w-4 h-4 text-yellow-400" />
                                  4. Instructions de style (optionnel)
                                </Label>
                                <VoiceInput 
                                  onTranscript={(text) => setAiStylePrompt(prev => prev + ' ' + text)}
                                  className="border-zinc-700"
                                />
                              </div>
                              <Textarea
                                value={aiStylePrompt}
                                onChange={(e) => setAiStylePrompt(e.target.value)}
                                placeholder="Ex: Style urbain, ambiance nocturne... ou utilisez le micro 🎤"
                                className="bg-zinc-800 border-zinc-700 h-20"
                              />
                            </div>
                            
                            {/* Bouton de génération */}
                            <Button 
                              onClick={generateSlideWithAI}
                              disabled={aiLoading || aiSelectedProducts.length === 0}
                              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                            >
                              {aiLoading ? (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                  Génération en cours...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4 mr-2" />
                                  Générer le slide
                                </>
                              )}
                            </Button>
                            
                            {/* Aperçu du contenu généré */}
                            {aiGeneratedContent && (
                              <div className="p-4 bg-zinc-800/50 rounded-lg border border-purple-500/30 space-y-4">
                                <h4 className="text-white font-medium flex items-center gap-2">
                                  <Sparkles className="w-4 h-4 text-purple-400" />
                                  Contenu généré
                                </h4>
                                
                                {/* Aperçu image */}
                                {aiGeneratedContent.image_base64 && (
                                  <div className="relative">
                                    <img 
                                      src={`data:image/png;base64,${aiGeneratedContent.image_base64}`}
                                      alt="Generated slide"
                                      className="w-full h-40 object-cover rounded-lg"
                                    />
                                    <Badge className="absolute top-2 right-2 bg-purple-500">IA</Badge>
                                  </div>
                                )}
                                
                                {/* Aperçu textes */}
                                <div className="grid grid-cols-3 gap-2 text-sm">
                                  <div>
                                    <p className="text-gray-400 text-xs">Titre FR</p>
                                    <p className="text-white">{aiGeneratedContent.title?.fr}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400 text-xs">Sous-titre FR</p>
                                    <p className="text-white">{aiGeneratedContent.subtitle?.fr}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400 text-xs">CTA</p>
                                    <p className="text-white">{aiGeneratedContent.cta_text?.fr}</p>
                                  </div>
                                </div>
                                
                                {/* Bouton pour appliquer */}
                                <Button 
                                  onClick={applyAiGeneratedSlide}
                                  className="w-full bg-green-600 hover:bg-green-700"
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Ajouter ce slide au slider
                                </Button>
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {sliderConfig.slides?.map((slide, index) => (
                      <div key={slide.id} id={`slide-editor-${index}`} className="p-4 bg-zinc-800/30 rounded-lg space-y-3 scroll-mt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">{index + 1}</Badge>
                            
                            {/* Vignette du produit source si IA */}
                            {slide.source_product_id && slide.source_product_image && (
                              <a 
                                href={`/product/${slide.source_product_slug || slide.source_product_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-2 py-1 bg-purple-500/20 rounded-lg hover:bg-purple-500/30 transition-colors"
                                title={`Voir le produit: ${slide.source_product_name}`}
                              >
                                <img 
                                  src={slide.source_product_image?.startsWith('/api/') 
                                    ? `${API_URL}${slide.source_product_image}` 
                                    : slide.source_product_image}
                                  alt={slide.source_product_name || 'Produit source'}
                                  className="w-8 h-8 object-cover rounded"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                                <span className="text-[10px] text-purple-300 max-w-[100px] truncate">
                                  {slide.source_product_name || 'Produit'}
                                </span>
                                <ExternalLink className="w-3 h-3 text-purple-400" />
                              </a>
                            )}
                            
                            <span className="text-white font-medium">{slide.title?.fr || 'Sans titre'}</span>
                            {/* Indicateur de statut */}
                            {slide.enabled ? (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-[10px]">
                                <Check className="w-3 h-3 mr-1" /> En ligne
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500/20 text-red-400 border-red-500/50 text-[10px]">
                                <XCircle className="w-3 h-3 mr-1" /> Hors ligne
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Bouton Prévisualiser */}
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => setPreviewSlide(slide)} 
                              title="Prévisualiser ce slide"
                              className="hover:bg-purple-500/20"
                            >
                              <Monitor className="w-4 h-4 text-purple-400" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditSlideModal(index)} title="Éditer ce slide">
                              <Pencil className="w-4 h-4 text-blue-400" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => moveSlide(index, 'up')} disabled={index === 0}>
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => moveSlide(index, 'down')} disabled={index === sliderConfig.slides.length - 1}>
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                            {/* Toggle En ligne / Hors ligne */}
                            <div className="flex items-center gap-2 px-2 py-1 rounded bg-zinc-700/50">
                              <span className="text-[10px] text-gray-400">{slide.enabled ? 'ON' : 'OFF'}</span>
                              <Switch
                                checked={slide.enabled}
                                onCheckedChange={(checked) => updateSlide(index, 'enabled', checked)}
                                data-testid={`slide-enabled-toggle-${index}`}
                              />
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => deleteSlide(index)}>
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Image upload */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-gray-200 mb-1 block">Image</Label>
                            <div className="flex gap-2">
                              <select
                                value={slide.image_source}
                                onChange={(e) => updateSlide(index, 'image_source', e.target.value)}
                                className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-sm"
                              >
                                <option value="upload">Upload</option>
                                <option value="url">URL</option>
                                <option value="ai_generated">IA (Phase 12)</option>
                              </select>
                              {slide.image_source === 'url' && (
                                <Input
                                  value={slide.image_url || ''}
                                  onChange={(e) => updateSlide(index, 'image_url', e.target.value)}
                                  className="bg-zinc-700 border-zinc-600 flex-1"
                                  placeholder="https://..."
                                />
                              )}
                              {slide.image_source === 'upload' && (
                                <Input
                                  type="file"
                                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                                  onChange={(e) => handleSliderImageUpload(index, e)}
                                  className="flex-1 text-xs"
                                />
                              )}
                            </div>
                            {slide.image_url && (
                              <div className="mt-2 relative">
                                {/* Aperçu avec position verticale */}
                                <div 
                                  className="h-24 rounded overflow-hidden border border-zinc-600"
                                  style={{
                                    backgroundImage: `url(${slide.image_url.startsWith('/api/') ? `${API_URL}${slide.image_url}` : slide.image_url})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: `center ${slide.image_position_y || 50}%`
                                  }}
                                >
                                  {/* Overlay avec texte pour prévisualisation */}
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex flex-col justify-end p-2">
                                    <p 
                                      className="text-white text-sm font-bold truncate"
                                      style={{ fontFamily: sliderConfig.title_font || 'Oswald' }}
                                    >
                                      {slide.title?.fr || 'Titre du slide'}
                                    </p>
                                    <p 
                                      className="text-white/80 text-xs"
                                      style={{ fontFamily: sliderConfig.cta_font || 'Inter' }}
                                    >
                                      {slide.cta_text?.fr || 'Bouton CTA'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Position verticale de l'image */}
                          <div>
                            <Label className="text-xs text-gray-200 mb-1 block">
                              Position verticale ({slide.image_position_y || 50}%)
                            </Label>
                            <div className="flex items-center gap-2">
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={slide.image_position_y || 50}
                                onChange={(e) => updateSlide(index, 'image_position_y', parseInt(e.target.value))}
                                className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                              />
                              <span className="text-xs text-gray-400 w-10 text-right">{slide.image_position_y || 50}%</span>
                            </div>
                            <p className="text-[10px] text-gray-500 mt-1">0% = haut, 50% = centre, 100% = bas</p>
                          </div>
                          
                          <div>
                            <Label className="text-xs text-gray-200 mb-1 block">
                              Filtre sombre ({slide.overlay_opacity || 0}%)
                            </Label>
                            <div className="flex items-center gap-2">
                              <input
                                type="range"
                                min="0"
                                max="70"
                                value={slide.overlay_opacity || 0}
                                onChange={(e) => updateSlide(index, 'overlay_opacity', parseInt(e.target.value))}
                                className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
                              />
                              <span className="text-xs text-gray-400 w-10 text-right">{slide.overlay_opacity || 0}%</span>
                            </div>
                            <p className="text-[10px] text-gray-500 mt-1">Assombrit l'image pour faire ressortir le texte</p>
                          </div>
                          
                          <div>
                            <Label className="text-xs text-gray-200 mb-1 block">Durée (sec)</Label>
                            <Input
                              type="number"
                              value={slide.duration_seconds}
                              onChange={(e) => updateSlide(index, 'duration_seconds', parseInt(e.target.value))}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                        </div>
                        
                        {/* Sélecteur de produit pour la vignette */}
                        <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700">
                          <Label className="text-xs text-gray-200 mb-2 block font-medium">
                            🏷️ Produit associé (vignette)
                          </Label>
                          <div className="flex gap-2 items-start">
                            {/* Image actuelle */}
                            {slide.source_product_image && (
                              <div className="flex-shrink-0">
                                <img 
                                  src={slide.source_product_image?.startsWith('/api/') 
                                    ? `${API_URL}${slide.source_product_image}` 
                                    : slide.source_product_image}
                                  alt={slide.source_product_name || 'Produit'}
                                  className="w-12 h-12 object-cover rounded border border-zinc-600"
                                />
                              </div>
                            )}
                            <div className="flex-1">
                              <select
                                value={slide.source_product_id || ''}
                                onChange={(e) => {
                                  const productId = e.target.value;
                                  if (!productId) {
                                    // Effacer le produit
                                    updateSlide(index, 'source_product_id', null);
                                    updateSlide(index, 'source_product_name', null);
                                    updateSlide(index, 'source_product_image', null);
                                    updateSlide(index, 'source_product_slug', null);
                                    updateSlide(index, 'source_product_price', null);
                                  } else {
                                    const selectedProduct = allProducts.find(p => p.id === productId || p._id === productId);
                                    if (selectedProduct) {
                                      const mainImg = selectedProduct.images?.main?.[0] || null;
                                      const price = selectedProduct.price?.final || selectedProduct.price?.base || 0;
                                      const priceStr = price ? `₪${price.toLocaleString()}` : null;
                                      
                                      updateSlide(index, 'source_product_id', productId);
                                      updateSlide(index, 'source_product_name', selectedProduct.name?.fr || selectedProduct.name?.en || 'Produit');
                                      updateSlide(index, 'source_product_image', mainImg);
                                      updateSlide(index, 'source_product_slug', selectedProduct.slug || productId);
                                      updateSlide(index, 'source_product_price', priceStr);
                                    }
                                  }
                                }}
                                className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white text-sm"
                              >
                                <option value="">-- Aucun produit (pas de vignette) --</option>
                                {allProducts.map(product => (
                                  <option key={product.id || product._id} value={product.id || product._id}>
                                    {product.name?.fr || product.name?.en || 'Produit'} 
                                    {product.price?.final ? ` - ₪${product.price.final}` : ''}
                                  </option>
                                ))}
                              </select>
                              {slide.source_product_name && (
                                <p className="text-[10px] text-gray-400 mt-1">
                                  {slide.source_product_name} • {slide.source_product_price || 'Prix non défini'}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Titles */}
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs text-gray-200">Titre FR</Label>
                            <Input
                              value={slide.title?.fr || ''}
                              onChange={(e) => updateSlide(index, 'title', e.target.value, 'fr')}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-200">Titre HE</Label>
                            <Input
                              dir="rtl"
                              value={slide.title?.he || ''}
                              onChange={(e) => updateSlide(index, 'title', e.target.value, 'he')}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-200">Titre EN</Label>
                            <Input
                              value={slide.title?.en || ''}
                              onChange={(e) => updateSlide(index, 'title', e.target.value, 'en')}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                        </div>
                        
                        {/* CTA */}
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <Label className="text-xs text-gray-200">CTA FR</Label>
                            <Input
                              value={slide.cta_text?.fr || ''}
                              onChange={(e) => updateSlide(index, 'cta_text', e.target.value, 'fr')}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-200">CTA HE</Label>
                            <Input
                              dir="rtl"
                              value={slide.cta_text?.he || ''}
                              onChange={(e) => updateSlide(index, 'cta_text', e.target.value, 'he')}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-200">CTA EN</Label>
                            <Input
                              value={slide.cta_text?.en || ''}
                              onChange={(e) => updateSlide(index, 'cta_text', e.target.value, 'en')}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-200">CTA URL</Label>
                            <Input
                              value={slide.cta_url || ''}
                              onChange={(e) => updateSlide(index, 'cta_url', e.target.value)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center justify-between">
                  <Button onClick={saveSlider} disabled={isSaving} data-testid="save-slider-button">
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  
                  {/* Bouton pour vider le cache du frontend */}
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      localStorage.removeItem('mutagim_store_settings');
                      localStorage.removeItem('mutagim_store_settings_time');
                      toast.success('Cache vidé ! Les modifications seront visibles immédiatement sur le site.');
                    }}
                    data-testid="clear-cache-button"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Vider le cache frontend
                  </Button>
                </div>
                
                {/* Modal de prévisualisation plein écran */}
                <Dialog open={!!previewSlide} onOpenChange={(open) => !open && setPreviewSlide(null)}>
                  <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 bg-black border-zinc-800">
                    <div className="relative w-full h-full min-h-[80vh]">
                      {/* Bouton fermer */}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setPreviewSlide(null)}
                        className="absolute top-4 right-4 z-50 bg-black/50 hover:bg-black/70 text-white"
                      >
                        <X className="w-6 h-6" />
                      </Button>
                      
                      {/* Contenu du slide en prévisualisation */}
                      {previewSlide && (
                        <div className="relative w-full h-full">
                          {/* Fond flou pour effet immersif */}
                          <div 
                            className="absolute inset-0 scale-110"
                            style={{
                              backgroundImage: `url(${previewSlide.image_url?.startsWith('/api/') ? `${API_URL}${previewSlide.image_url}` : previewSlide.image_url})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              filter: 'blur(40px)',
                              opacity: 0.6
                            }}
                          />
                          
                          {/* Image principale */}
                          <div 
                            className="absolute inset-0"
                            style={{
                              backgroundImage: `url(${previewSlide.image_url?.startsWith('/api/') ? `${API_URL}${previewSlide.image_url}` : previewSlide.image_url})`,
                              backgroundSize: `${sliderConfig.image_zoom || 100}%`,
                              backgroundPosition: `center ${previewSlide.image_position_y || 50}%`,
                              backgroundRepeat: 'no-repeat'
                            }}
                          />
                          
                          {/* Overlay gradient */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                          
                          {/* Contenu texte */}
                          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                            <h1 
                              className="text-5xl md:text-7xl font-bold mb-4 drop-shadow-lg"
                              style={{ 
                                fontFamily: sliderConfig.title_font || 'Oswald',
                                color: sliderConfig.title_color || '#FFFFFF'
                              }}
                            >
                              {previewSlide.title?.fr || 'Titre du slide'}
                            </h1>
                            <p 
                              className="text-xl md:text-2xl mb-8 drop-shadow"
                              style={{ color: sliderConfig.subtitle_color || '#D1D5DB' }}
                            >
                              {previewSlide.subtitle?.fr || 'Sous-titre'}
                            </p>
                            <button
                              className="px-8 py-3 rounded-full text-lg font-medium transition-transform hover:scale-105"
                              style={{ 
                                fontFamily: sliderConfig.cta_font || 'Inter',
                                backgroundColor: sliderConfig.cta_bg_color || '#FFFFFF',
                                color: sliderConfig.cta_text_color || '#000000'
                              }}
                            >
                              {previewSlide.cta_text?.fr || 'Bouton CTA'}
                            </button>
                          </div>
                          
                          {/* Indicateur de statut */}
                          <div className="absolute bottom-4 left-4 flex items-center gap-2">
                            {previewSlide.enabled ? (
                              <Badge className="bg-green-500/80 text-white border-0">
                                <Check className="w-4 h-4 mr-1" /> Ce slide est EN LIGNE
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500/80 text-white border-0">
                                <XCircle className="w-4 h-4 mr-1" /> Ce slide est HORS LIGNE
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* FOOTER TAB */}
          <TabsContent value="footer">
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Columns className="w-5 h-5 text-primary" />
                  Configuration du Footer
                </CardTitle>
                <CardDescription>Colonnes, copyright et newsletter</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                  <div>
                    <div className="text-white font-medium">Newsletter</div>
                    <div className="text-sm text-gray-200">Afficher le formulaire d'inscription</div>
                  </div>
                  <Switch
                    checked={footerConfig.show_newsletter}
                    onCheckedChange={(checked) => setFooterConfig(prev => ({ ...prev, show_newsletter: checked }))}
                  />
                </div>
                
                {/* Copyright */}
                <div>
                  <Label className="text-white mb-2 block">Copyright</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      value={footerConfig.copyright_text?.fr || ''}
                      onChange={(e) => setFooterConfig(prev => ({
                        ...prev,
                        copyright_text: { ...prev.copyright_text, fr: e.target.value }
                      }))}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="FR"
                    />
                    <Input
                      dir="rtl"
                      value={footerConfig.copyright_text?.he || ''}
                      onChange={(e) => setFooterConfig(prev => ({
                        ...prev,
                        copyright_text: { ...prev.copyright_text, he: e.target.value }
                      }))}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="HE"
                    />
                    <Input
                      value={footerConfig.copyright_text?.en || ''}
                      onChange={(e) => setFooterConfig(prev => ({
                        ...prev,
                        copyright_text: { ...prev.copyright_text, en: e.target.value }
                      }))}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="EN"
                    />
                  </div>
                </div>
                
                {/* Columns */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-white">Colonnes</Label>
                    <Button variant="outline" size="sm" onClick={addFooterColumn}>
                      <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {footerConfig.columns?.map((col, colIndex) => (
                      <div key={col.id} className="p-4 bg-zinc-800/30 rounded-lg space-y-3">
                        <div className="flex items-center gap-2">
                          <Input
                            value={col.title?.fr || ''}
                            onChange={(e) => {
                              const newCols = [...footerConfig.columns];
                              newCols[colIndex] = { ...col, title: { ...col.title, fr: e.target.value } };
                              setFooterConfig(prev => ({ ...prev, columns: newCols }));
                            }}
                            className="bg-zinc-700 border-zinc-600 flex-1"
                            placeholder="Titre colonne"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const newCols = footerConfig.columns.filter((_, i) => i !== colIndex);
                              setFooterConfig(prev => ({ ...prev, columns: newCols }));
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                        {/* Links in column */}
                        <div className="pl-4 space-y-2">
                          {col.links?.map((link, linkIndex) => (
                            <div key={link.id} className="flex items-center gap-2">
                              <Input
                                value={link.label?.fr || ''}
                                className="bg-zinc-700 border-zinc-600 flex-1"
                                placeholder="Label"
                                onChange={(e) => {
                                  const newCols = [...footerConfig.columns];
                                  const newLinks = [...col.links];
                                  newLinks[linkIndex] = { ...link, label: { ...link.label, fr: e.target.value } };
                                  newCols[colIndex] = { ...col, links: newLinks };
                                  setFooterConfig(prev => ({ ...prev, columns: newCols }));
                                }}
                              />
                              <Input
                                value={link.url || ''}
                                className="bg-zinc-700 border-zinc-600 w-32"
                                placeholder="URL"
                                onChange={(e) => {
                                  const newCols = [...footerConfig.columns];
                                  const newLinks = [...col.links];
                                  newLinks[linkIndex] = { ...link, url: e.target.value };
                                  newCols[colIndex] = { ...col, links: newLinks };
                                  setFooterConfig(prev => ({ ...prev, columns: newCols }));
                                }}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  const newCols = [...footerConfig.columns];
                                  newCols[colIndex] = { ...col, links: col.links.filter((_, i) => i !== linkIndex) };
                                  setFooterConfig(prev => ({ ...prev, columns: newCols }));
                                }}
                              >
                                <Trash2 className="w-3 h-3 text-red-400" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newLink = {
                                id: `link-${Date.now()}`,
                                label: { fr: '', he: '', en: '' },
                                url: '/',
                                order: col.links?.length || 0,
                                enabled: true
                              };
                              const newCols = [...footerConfig.columns];
                              newCols[colIndex] = { ...col, links: [...(col.links || []), newLink] };
                              setFooterConfig(prev => ({ ...prev, columns: newCols }));
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Lien
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <Button onClick={saveFooter} disabled={isSaving}>
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* PAGES TAB */}
          <TabsContent value="pages">
            <div className="grid grid-cols-3 gap-6">
              {/* List */}
              <Card className="bg-zinc-900/80 border-zinc-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-lg">Pages</CardTitle>
                    <Button variant="outline" size="sm" onClick={createNewPage} data-testid="create-page-button">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {staticPages.map(page => (
                      <button
                        key={page.slug}
                        onClick={() => setSelectedPage(page)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          selectedPage?.slug === page.slug
                            ? 'bg-primary/20 text-primary'
                            : 'bg-zinc-800/30 text-white hover:bg-zinc-800/50'
                        }`}
                      >
                        <div className="font-medium">{page.title?.fr || page.slug}</div>
                        <div className="text-xs text-gray-200">/{page.slug}</div>
                      </button>
                    ))}
                    {staticPages.length === 0 && (
                      <p className="text-gray-200 text-sm text-center py-4">Aucune page</p>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Editor */}
              <Card className="col-span-2 bg-zinc-900/80 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    {selectedPage ? 'Éditeur de page' : 'Sélectionnez une page'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedPage ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white mb-2 block">Slug (URL)</Label>
                          <Input
                            value={selectedPage.slug}
                            onChange={(e) => setSelectedPage(prev => ({ ...prev, slug: e.target.value }))}
                            className="bg-zinc-800 border-zinc-700"
                            data-testid="page-slug-input"
                          />
                        </div>
                        <div className="flex items-center gap-4">
                          <Switch
                            checked={selectedPage.enabled}
                            onCheckedChange={(checked) => setSelectedPage(prev => ({ ...prev, enabled: checked }))}
                          />
                          <span className="text-white">Active</span>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block">Titre</Label>
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            value={selectedPage.title?.fr || ''}
                            onChange={(e) => setSelectedPage(prev => ({
                              ...prev,
                              title: { ...prev.title, fr: e.target.value }
                            }))}
                            className="bg-zinc-800 border-zinc-700"
                            placeholder="FR"
                          />
                          <Input
                            dir="rtl"
                            value={selectedPage.title?.he || ''}
                            onChange={(e) => setSelectedPage(prev => ({
                              ...prev,
                              title: { ...prev.title, he: e.target.value }
                            }))}
                            className="bg-zinc-800 border-zinc-700"
                            placeholder="HE"
                          />
                          <Input
                            value={selectedPage.title?.en || ''}
                            onChange={(e) => setSelectedPage(prev => ({
                              ...prev,
                              title: { ...prev.title, en: e.target.value }
                            }))}
                            className="bg-zinc-800 border-zinc-700"
                            placeholder="EN"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block">Contenu (FR)</Label>
                        <SimpleRichTextEditor
                          value={selectedPage.content?.fr || ''}
                          onChange={(value) => setSelectedPage(prev => ({
                            ...prev,
                            content: { ...prev.content, fr: value }
                          }))}
                          placeholder="Entrez le contenu HTML de la page..."
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <Button onClick={() => savePage(selectedPage)} disabled={isSaving}>
                          <Save className="w-4 h-4 mr-2" />
                          Sauvegarder
                        </Button>
                        <Button variant="outline" onClick={() => setSelectedPage(null)}>
                          Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className={cn("text-center py-12", isLight ? "text-gray-500" : "text-gray-200")}>
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Sélectionnez une page à éditer ou créez-en une nouvelle</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Modal d'édition de slide */}
      {editingSlide && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-zinc-700">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-700 sticky top-0 bg-zinc-900">
              <h3 className="text-lg font-semibold text-white">Éditer le slide</h3>
              <Button variant="ghost" size="icon" onClick={closeEditSlideModal}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Contenu */}
            <div className="p-4 space-y-4">
              {/* Aperçu */}
              {editingSlide.image_url && (
                <div 
                  className="w-full aspect-[16/6] rounded-lg overflow-hidden border border-zinc-600"
                  style={{
                    backgroundImage: `url(${editingSlide.image_url.startsWith('/api/') ? `${API_URL}${editingSlide.image_url}` : editingSlide.image_url})`,
                    backgroundSize: `${sliderConfig.image_zoom || 100}%`,
                    backgroundPosition: `center ${editingSlide.image_position_y || 50}%`,
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  <div className="w-full h-full bg-gradient-to-t from-black/60 via-transparent to-transparent flex flex-col justify-end p-4">
                    <p className="text-white text-2xl font-bold" style={{ fontFamily: sliderConfig.title_font || 'Oswald' }}>
                      {editingSlide.title?.fr || 'Titre du slide'}
                    </p>
                    <p className="text-white/70 text-sm mt-1">{editingSlide.subtitle?.fr || ''}</p>
                  </div>
                </div>
              )}
              
              {/* Position verticale */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs text-gray-200">Position verticale de l'image</Label>
                  <span className="text-xs text-gray-400">{editingSlide.image_position_y || 50}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={editingSlide.image_position_y || 50}
                  onChange={(e) => updateEditingSlide('image_position_y', parseInt(e.target.value))}
                  className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                />
              </div>
              
              {/* Titre */}
              <div>
                <Label className="text-xs text-gray-200 mb-1 block">Titre (FR)</Label>
                <Input
                  value={editingSlide.title?.fr || ''}
                  onChange={(e) => updateEditingSlide('title', e.target.value, 'fr')}
                  className="bg-zinc-700 border-zinc-600"
                  placeholder="Titre du slide en français"
                />
              </div>
              
              <div>
                <Label className="text-xs text-gray-200 mb-1 block">Titre (EN)</Label>
                <Input
                  value={editingSlide.title?.en || ''}
                  onChange={(e) => updateEditingSlide('title', e.target.value, 'en')}
                  className="bg-zinc-700 border-zinc-600"
                  placeholder="Slide title in English"
                />
              </div>
              
              {/* Sous-titre */}
              <div>
                <Label className="text-xs text-gray-200 mb-1 block">Sous-titre (FR)</Label>
                <Textarea
                  value={editingSlide.subtitle?.fr || ''}
                  onChange={(e) => updateEditingSlide('subtitle', e.target.value, 'fr')}
                  className="bg-zinc-700 border-zinc-600 min-h-[60px]"
                  placeholder="Description courte"
                />
              </div>
              
              {/* CTA */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-200 mb-1 block">Texte du bouton (FR)</Label>
                  <Input
                    value={editingSlide.cta_text?.fr || ''}
                    onChange={(e) => updateEditingSlide('cta_text', e.target.value, 'fr')}
                    className="bg-zinc-700 border-zinc-600"
                    placeholder="Ex: Découvrir"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-200 mb-1 block">Lien du bouton</Label>
                  <Input
                    value={editingSlide.cta_url || ''}
                    onChange={(e) => updateEditingSlide('cta_url', e.target.value)}
                    className="bg-zinc-700 border-zinc-600"
                    placeholder="/products ou /collections/..."
                  />
                </div>
              </div>
              
              {/* Durée */}
              <div>
                <Label className="text-xs text-gray-200 mb-1 block">Durée d'affichage (secondes)</Label>
                <Input
                  type="number"
                  value={(editingSlide.duration || 5000) / 1000}
                  onChange={(e) => updateEditingSlide('duration', parseFloat(e.target.value) * 1000)}
                  className="bg-zinc-700 border-zinc-600 w-24"
                  min={2}
                  max={20}
                />
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-700 sticky bottom-0 bg-zinc-900">
              <Button variant="outline" onClick={closeEditSlideModal}>
                Annuler
              </Button>
              <Button onClick={saveEditedSlide} className="bg-purple-600 hover:bg-purple-700">
                <Save className="w-4 h-4 mr-2" />
                Appliquer les modifications
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminPageWrapper>
  );
}

export default AdminBrandSettingsPage;
